export type MonthlyFinanceKind = 'actual' | 'projected';

/**
 * Which month an amount counts in. Accrual spreads it over the days it covers; cash puts
 * it whole in the month the money moved. Projections read the same under both.
 */
export type MonthAttribution = 'accrual' | 'cash';

export const MONTH_ATTRIBUTIONS: MonthAttribution[] = ['accrual', 'cash'];

export type MonthlyFinanceAuditAction = 'added' | 'changed' | 'removed';

export interface MonthlyFinanceMonthValue {
  year: number;
  month: number;
  computed_amount: number | null;
  overridden_amount: number | null;
  effective_amount: number | null;
}

export interface MonthlyFinanceItem {
  id: number;
  key: string;
  label: string;
  values: MonthlyFinanceMonthValue[];
}

export interface MonthlyFinanceSubcategory {
  key: string;
  label: string;
  items: MonthlyFinanceItem[];
}

export interface MonthlyFinanceCategory {
  key: 'income' | 'operating_expenses' | string;
  label: string;
  subcategories: MonthlyFinanceSubcategory[];
}

export interface MonthlyFinanceProfitAndLoss {
  categories: MonthlyFinanceCategory[];
}

export interface MonthlyFinanceProfitAndLossParams {
  start_year: number;
  start_month: number;
  end_year: number;
  end_month: number;
  address_id?: number;
  kind?: MonthlyFinanceKind;
  month_attribution?: MonthAttribution;
}

export interface MonthlyFinanceOverrideEntry {
  address_id: number;
  monthly_finance_type_key: string;
  kind?: MonthlyFinanceKind;
  /** An override belongs to the reading it was typed in; accrual when omitted. */
  month_attribution?: MonthAttribution;
  year: number;
  month: number;
  overridden_amount: number | null;
}

export interface MonthlyFinanceAuditUser {
  id: number;
  type: string;
}

export interface MonthlyFinanceAuditTypeRef {
  id: number;
  key: string;
  label: string;
}

export interface MonthlyFinanceAuditRef {
  id: number;
  address_id: number;
  monthly_finance_type: MonthlyFinanceAuditTypeRef;
  kind: MonthlyFinanceKind;
  year: number;
  month: number;
}

export interface MonthlyFinanceAudit {
  id: number;
  action: MonthlyFinanceAuditAction;
  old_value: number | null;
  new_value: number | null;
  created_at: string;
  monthly_finance: MonthlyFinanceAuditRef;
  user: MonthlyFinanceAuditUser | null;
}

export interface MonthlyFinanceAuditsParams {
  page?: number;
  per_page?: number;
  address_id?: number;
  start_year?: number;
  start_month?: number;
  end_year?: number;
  end_month?: number;
  kind?: MonthlyFinanceKind;
  month_attribution?: MonthAttribution;
}

export interface FinanceKpisData {
  gross_revenue: number;
  operating_expenses: number;
  net_operating_income: number;
  occupancy: number | null;
  avg_nightly_rate: number | null;
}

export interface FinanceTrendsMonth {
  year: number;
  month: number;
  actual_revenue: number;
  projected_revenue: number;
  actual_net_income: number;
  projected_net_income: number;
}

export interface FinanceTrendsData {
  months: FinanceTrendsMonth[];
}

export interface FinanceSummaryRow {
  subcategory: string;
  label: string;
  actual: number;
  projected: number;
  variance: number;
  variance_percent: number | null;
}

export interface FinanceSummaryData {
  rows: FinanceSummaryRow[];
}

export interface OperationsKpisData {
  avg_star_rating: number | null;
  turnover_completion_rate: number | null;
  account_guest_response_time_minutes: number | null;
  account_homekeeper_response_time_minutes: number | null;
  guest_agent_enabled: boolean;
  homekeeper_agent_enabled: boolean;
  inquiry_response_time_minutes: number | null;
  inquiries_answered_by_tidy: boolean;
}

export type FinanceWidgetKey = 'finance_kpis' | 'finance_trends' | 'finance_summary' | 'operations_kpis';

export interface FinanceWidgetEnvelope<K extends FinanceWidgetKey = FinanceWidgetKey, D = unknown> {
  key: K;
  data: D;
}

export interface FinanceWidgetsRequest {
  finance_kpis?: { start_date: string; end_date: string; address_id?: number; month_attribution: MonthAttribution };
  finance_trends?: { start_date: string; end_date: string; address_id?: number; month_attribution: MonthAttribution };
  finance_summary?: { year: number; address_id?: number; month_attribution: MonthAttribution };
  operations_kpis?: { start_date: string; end_date: string; address_id?: number };
}
